import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Stack,
  InputAdornment,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
  CardGiftcard as CardGiftcardIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  QrCode as QrCodeIcon,
  Code as CodeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  CheckCircleOutline,
  HourglassEmpty,
  Block,
  Settings as ProcessingIcon,
  LocalOffer,
  Category,
  Phone,
  Email,
  LocationOn,
  Timer,
  ConfirmationNumber,
  CreditCard,
  Redeem,
  Info,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import api from '../services/api';

// Dynamic redemption interface that adapts to benefit metadata
interface Redemption {
  id: string;
  user_id: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  user_phone?: string;
  benefit_id: string;
  benefit_name: string;
  benefit_description?: string;
  redemption_code: string;
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'expired';
  redemption_date: string;
  created_at: string;
  updated_at?: string;
  
  // Dynamic fields from benefit metadata
  company_name?: string;
  company_logo?: string;
  company_website?: string;
  benefit_type?: string;
  discount_value?: string;
  redemption_method?: string;
  promo_code?: string;
  valid_from?: string;
  valid_to?: string;
  usage_limit?: number;
  terms?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  location?: string;
  
  // Additional metadata
  metadata?: any;
  notes?: string;
  admin_notes?: string;
  rejection_reason?: string;
}

// Configuration for different benefit types
const BENEFIT_TYPE_CONFIG: { [key: string]: any } = {
  'discount': {
    icon: <LocalOffer />,
    color: 'success',
    displayFields: ['discount_value', 'promo_code', 'valid_to'],
    label: 'Discount',
  },
  'voucher': {
    icon: <ConfirmationNumber />,
    color: 'primary',
    displayFields: ['redemption_value', 'voucher_code', 'expires_at'],
    label: 'Voucher',
  },
  'service': {
    icon: <BusinessIcon />,
    color: 'info',
    displayFields: ['service_type', 'location', 'booking_required'],
    label: 'Service',
  },
  'membership': {
    icon: <CreditCard />,
    color: 'secondary',
    displayFields: ['membership_type', 'duration', 'start_date'],
    label: 'Membership',
  },
  'product': {
    icon: <Redeem />,
    color: 'warning',
    displayFields: ['product_name', 'quantity', 'delivery_method'],
    label: 'Product',
  },
  'cashback': {
    icon: <MoneyIcon />,
    color: 'success',
    displayFields: ['cashback_percentage', 'max_amount', 'payment_method'],
    label: 'Cashback',
  },
  'insurance': {
    icon: <AssignmentIcon />,
    color: 'default',
    displayFields: ['coverage_type', 'policy_number', 'valid_period'],
    label: 'Insurance',
  },
};

const RedemptionsDynamic: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  
  const [filters, setFilters] = useState({
    status: 'all',
    benefitType: 'all',
    company: 'all',
    searchTerm: '',
    dateRange: 'all',
  });

  const [companies, setCompanies] = useState<string[]>([]);
  const [benefitTypes, setBenefitTypes] = useState<string[]>([]);
  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);

  useEffect(() => {
    fetchRedemptions();
  }, [filters]);

  const fetchRedemptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/admin/redemptions');
      const data = response.data;
      
      // Process redemptions to extract metadata
      const processedRedemptions = data.map((item: any) => {
        const redemption: Redemption = {
          id: item.id,
          user_id: item.user_id,
          user_first_name: item.user_first_name,
          user_last_name: item.user_last_name,
          user_email: item.user_email,
          user_phone: item.user_phone,
          benefit_id: item.benefit_id,
          benefit_name: item.benefit_name,
          benefit_description: item.benefit_description,
          redemption_code: item.redemption_code || `RED${Date.now()}`,
          status: item.status || 'pending',
          redemption_date: item.redeemed_at || item.created_at,
          created_at: item.created_at,
          updated_at: item.updated_at,
          notes: item.notes,
          admin_notes: item.admin_notes,
          rejection_reason: item.rejection_reason,
        };
        
        // Parse benefit metadata
        if (item.benefit_metadata) {
          try {
            const metadata = typeof item.benefit_metadata === 'string' 
              ? JSON.parse(item.benefit_metadata) 
              : item.benefit_metadata;
            
            // Extract all metadata fields dynamically
            Object.keys(metadata).forEach(key => {
              redemption[key as keyof Redemption] = metadata[key];
            });
          } catch (e) {
            console.error('Error parsing metadata:', e);
          }
        }
        
        // Parse redemption metadata
        if (item.metadata) {
          try {
            const metadata = typeof item.metadata === 'string'
              ? JSON.parse(item.metadata)
              : item.metadata;
            redemption.metadata = metadata;
          } catch (e) {
            console.error('Error parsing redemption metadata:', e);
          }
        }
        
        return redemption;
      });
      
      setRedemptions(processedRedemptions);
      
      // Extract unique values for filters
      const uniqueCompanies = Array.from(new Set(processedRedemptions
        .map((r: Redemption) => r.company_name)
        .filter(Boolean))) as string[];
      const uniqueTypes = Array.from(new Set(processedRedemptions
        .map((r: Redemption) => r.benefit_type)
        .filter(Boolean))) as string[];
      
      setCompanies(uniqueCompanies);
      setBenefitTypes(uniqueTypes);
      
      // Determine dynamic columns based on benefit types present
      const columnsSet = new Set<string>();
      processedRedemptions.forEach((r: Redemption) => {
        const config = BENEFIT_TYPE_CONFIG[r.benefit_type || ''];
        if (config?.displayFields) {
          config.displayFields.forEach((field: string) => {
            if (r[field as keyof Redemption]) {
              columnsSet.add(field);
            }
          });
        }
      });
      setDynamicColumns(Array.from(columnsSet));
      
    } catch (err: any) {
      console.error('Error fetching redemptions:', err);
      setError('Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredRedemptions = () => {
    let filtered = [...redemptions];
    
    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }
    
    if (filters.benefitType !== 'all') {
      filtered = filtered.filter(r => r.benefit_type === filters.benefitType);
    }
    
    if (filters.company !== 'all') {
      filtered = filtered.filter(r => r.company_name === filters.company);
    }
    
    if (filters.searchTerm) {
      const search = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.user_first_name?.toLowerCase().includes(search) ||
        r.user_last_name?.toLowerCase().includes(search) ||
        r.user_email?.toLowerCase().includes(search) ||
        r.benefit_name?.toLowerCase().includes(search) ||
        r.redemption_code?.toLowerCase().includes(search) ||
        r.company_name?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      await api.put(`/admin/redemptions/${id}`, {
        status: newStatus,
        admin_notes: actionNotes,
      });

      await fetchRedemptions();
      setActionDialog(false);
      setActionNotes('');
      setSelectedRedemption(null);
    } catch (err: any) {
      setError('Failed to update redemption status');
    }
  };

  const getStatusIcon = (status: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      pending: <HourglassEmpty />,
      approved: <CheckCircleOutline />,
      processing: <ProcessingIcon />,
      completed: <CheckCircleIcon />,
      rejected: <Block />,
      expired: <ScheduleIcon />,
    };
    return icons[status] || <PendingIcon />;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: any } = {
      pending: 'warning',
      approved: 'info',
      processing: 'default',
      completed: 'success',
      rejected: 'error',
      expired: 'default',
    };
    return colors[status] || 'default';
  };

  const getBenefitTypeConfig = (type: string) => {
    return BENEFIT_TYPE_CONFIG[type] || {
      icon: <CardGiftcardIcon />,
      color: 'default',
      displayFields: [],
      label: type || 'Benefit',
    };
  };

  const formatFieldValue = (field: string, value: any): string => {
    if (!value) return '-';
    
    // Format based on field name
    if (field.includes('date') || field.includes('_at')) {
      return new Date(value).toLocaleDateString();
    }
    if (field.includes('value') || field.includes('amount') || field.includes('price')) {
      return `R${parseFloat(value).toLocaleString()}`;
    }
    if (field.includes('percentage') || field.includes('discount')) {
      return value.includes('%') ? value : `${value}%`;
    }
    
    return value.toString();
  };

  const formatFieldName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredRedemptions = getFilteredRedemptions();

  // Calculate dynamic stats based on actual data
  const stats = {
    total: redemptions.length,
    pending: redemptions.filter(r => r.status === 'pending').length,
    approved: redemptions.filter(r => r.status === 'approved').length,
    completed: redemptions.filter(r => r.status === 'completed').length,
    byType: {} as { [key: string]: number },
  };

  // Count by benefit type
  benefitTypes.forEach(type => {
    stats.byType[type] = redemptions.filter(r => r.benefit_type === type).length;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Redemptions Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time tracking of all benefit redemptions
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Dynamic Stats Based on Benefit Types */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Total Redemptions
                  </Typography>
                  <Typography variant="h4">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CardGiftcardIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Pending Approval
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <PendingIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Dynamic stats for top benefit types */}
        {benefitTypes.slice(0, 2).map(type => {
          const config = getBenefitTypeConfig(type);
          return (
            <Grid item xs={12} sm={6} md={3} key={type}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        {config.label} Redemptions
                      </Typography>
                      <Typography variant="h4">
                        {stats.byType[type] || 0}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: `${config.color}.main` }}>
                      {config.icon}
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search users, benefits, codes..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Benefit Type</InputLabel>
                <Select
                  value={filters.benefitType}
                  onChange={(e) => setFilters({ ...filters, benefitType: e.target.value })}
                  label="Benefit Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {benefitTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {getBenefitTypeConfig(type).label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={filters.company}
                  onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                  label="Company"
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {companies.map(company => (
                    <MenuItem key={company} value={company}>{company}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                  label="Date Range"
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dynamic Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Benefit</TableCell>
              <TableCell>Type</TableCell>
              {/* Dynamic columns based on benefit types */}
              {dynamicColumns.slice(0, 2).map(col => (
                <TableCell key={col}>{formatFieldName(col)}</TableCell>
              ))}
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRedemptions.map((redemption) => {
              const config = getBenefitTypeConfig(redemption.benefit_type || '');
              return (
                <TableRow key={redemption.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {redemption.redemption_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {redemption.user_first_name} {redemption.user_last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {redemption.user_email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {redemption.benefit_name}
                      </Typography>
                      {redemption.company_name && (
                        <Typography variant="caption" color="text.secondary">
                          {redemption.company_name}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={config.icon}
                      label={config.label}
                      size="small" 
                      color={config.color}
                      variant="outlined"
                    />
                  </TableCell>
                  {/* Dynamic value columns */}
                  {dynamicColumns.slice(0, 2).map(col => (
                    <TableCell key={col}>
                      <Typography variant="body2">
                        {formatFieldValue(col, redemption[col as keyof Redemption])}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(redemption.redemption_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(redemption.status)}
                      label={redemption.status}
                      size="small"
                      color={getStatusColor(redemption.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRedemption(redemption);
                            setDetailsDialog(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {redemption.status === 'pending' && (
                        <Tooltip title="Take Action">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedRedemption(redemption);
                              setActionDialog(true);
                            }}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dynamic Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Redemption Details</Typography>
            {selectedRedemption && (
              <Chip
                icon={getStatusIcon(selectedRedemption.status)}
                label={selectedRedemption.status}
                color={getStatusColor(selectedRedemption.status)}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRedemption && (
            <Box>
              {/* User Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">User Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body2">
                            {selectedRedemption.user_first_name} {selectedRedemption.user_last_name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Email fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">
                            {selectedRedemption.user_email}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    {selectedRedemption.user_phone && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Phone fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Phone
                            </Typography>
                            <Typography variant="body2">
                              {selectedRedemption.user_phone}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Benefit Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Benefit Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Benefit Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedRedemption.benefit_name}
                      </Typography>
                      {selectedRedemption.benefit_description && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedRedemption.benefit_description}
                        </Typography>
                      )}
                    </Grid>
                    
                    {/* Dynamic fields based on benefit type */}
                    {Object.keys(selectedRedemption).map(key => {
                      // Skip standard fields
                      if (['id', 'user_id', 'benefit_id', 'status', 'created_at', 'updated_at', 
                           'user_first_name', 'user_last_name', 'user_email', 'user_phone',
                           'benefit_name', 'benefit_description', 'redemption_code', 
                           'redemption_date', 'notes', 'admin_notes', 'metadata'].includes(key)) {
                        return null;
                      }
                      
                      const value = selectedRedemption[key as keyof Redemption];
                      if (!value) return null;
                      
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFieldName(key)}
                          </Typography>
                          <Typography variant="body2">
                            {formatFieldValue(key, value)}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Redemption Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Redemption Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Redemption Code
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedRedemption.redemption_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Redemption Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedRedemption.redemption_date).toLocaleString()}
                      </Typography>
                    </Grid>
                    {selectedRedemption.notes && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          User Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedRedemption.notes}
                        </Typography>
                      </Grid>
                    )}
                    {selectedRedemption.admin_notes && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Admin Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedRedemption.admin_notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Additional Metadata */}
              {selectedRedemption.metadata && Object.keys(selectedRedemption.metadata).length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Additional Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {Object.entries(selectedRedemption.metadata).map(([key, value]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFieldName(key)}
                          </Typography>
                          <Typography variant="body2">
                            {formatFieldValue(key, value)}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>
            Close
          </Button>
          {selectedRedemption?.status === 'pending' && (
            <Button
              variant="contained"
              onClick={() => {
                setDetailsDialog(false);
                setActionDialog(true);
              }}
            >
              Take Action
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Process Redemption
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Redemption: <strong>{selectedRedemption?.redemption_code}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              User: <strong>{selectedRedemption?.user_first_name} {selectedRedemption?.user_last_name}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Benefit: <strong>{selectedRedemption?.benefit_name}</strong>
            </Typography>
            
            <TextField
              label="Admin Notes"
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 3 }}
              placeholder="Add any notes about this action..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleUpdateStatus(selectedRedemption!.id, 'rejected')}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleUpdateStatus(selectedRedemption!.id, 'approved')}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedemptionsDynamic;